import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Container, Row, Col, Form, Button, InputGroup, Alert, Card } from 'react-bootstrap';
import { UserContext } from '../Contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaTrash, FaPlus, FaRedo, FaEraser } from 'react-icons/fa';
import { Helmet } from 'react-helmet';

const RunPage = () => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [leftSamples, setLeftSamples] = useState([]);
    const [rightSamples, setRightSamples] = useState(['Mbuti.DG']);
    const [runType, setRunType] = useState('static');
    const [searchTermLeft, setSearchTermLeft] = useState('');
    const [searchTermRight, setSearchTermRight] = useState('');
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [sampleName, setSampleName] = useState('');
    const [samples, setSamples] = useState([]);
    const [populations, setPopulations] = useState(2)
    const [submitted, setSubmitted] = useState(false);

    const handleAddSample = (side) => {
        if (searchTermLeft || searchTermRight) {
            if (side === 'left') {
                if (samples.filter((sample) => sample.sample_name === searchTermLeft).length > 0) {
                    setLeftSamples([...leftSamples, searchTermLeft]);
                    setSearchTermLeft('');
                }
            } else if (side === 'right') {
                if (samples.filter((sample) => sample.sample_name === searchTermRight).length > 0) {
                    setRightSamples([...rightSamples, searchTermRight]);
                    setSearchTermRight('')
                }
            }
        }
    };

    useEffect(() => {
        // Check if user is populated and has a token
        if (!user?.token) {
            // Perform the redirect or other action if token is missing
            navigate('/login_required');
        }
    }, [user, navigate]);

    const runErrorCheck = () => {
        if (rightSamples.length < 1 || leftSamples.length < 1) {
            setMessage('Samples cannot be empty')
            return true
        }

        if (rightSamples.some(item => leftSamples.includes(item))) {
            setMessage('Same populations cannot be used in rights & lefts')
            return true
        }

        if (rightSamples.length < 4) {
            setMessage('Right Pops need atleast more than 4 sample(s)')
            return true
        }

        if (leftSamples.length < 3) {
            setMessage('Left Pops need atleast 3 sample(s)')
            return true
        }

        return false
    }

    // Function to handle search and API call
    const fetchSamples = useCallback(async () => {
        setLoading(true);
        try {
            // Build the query string with search parameters
            const queryParams = new URLSearchParams();
            if (sampleName) queryParams.append('sampleName', sampleName);

            // Call the API with the query parameters
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}samples/search?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user?.token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error('Failed to fetch samples');
            }

            const data = await response.json();
            setSamples(data);
        } catch (err) {
            console.log('')
        } finally {
            setLoading(false);
        }
    }, [sampleName, setLoading, user?.token]);

    // Effect to call the API whenever search params change
    useEffect(() => {
        if (searchTermLeft || searchTermRight) {
            setSampleName(searchTermLeft ?? searchTermRight)
        }

        fetchSamples();
    }, [searchTermLeft, searchTermRight, sampleName, fetchSamples]);


    const handleDelete = (sampleSet, sampleDelete) => {
        if (sampleSet === "right") {
            const newSamples = rightSamples.filter((sample) => sample !== sampleDelete);
            setRightSamples(newSamples);
        } else if (sampleSet === "left") {
            const newSamples = leftSamples.filter((sample) => sample !== sampleDelete);
            setLeftSamples(newSamples);
        } else {
            console.log('Set not found!')
        }
    };

    const preloadRights = () => {
        setRightSamples([
            'Mbuti.DG',
            'EHG',
            'CHG',
            'WSHG',
            'China_Paleolithic',
            'Onge.DG',
            'Turkey_N',
            'Levant_PPN',
            'Iran_GanjDareh_N',
            'Mongolia_North_N',
            'Serbia_IronGates_Mesolithic'
        ])
    }

    const clearRights = () => {
        setRightSamples([
            'Mbuti.DG',
        ])
    }

    const runJob = async () => {
        const errors = runErrorCheck()
        if (errors) {
            return
        }

        const payload = {
            run_type: runType,
            left_pops: leftSamples,
            right_pops: rightSamples,
            user: user?.uuid
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}qpadm_runs/qpadm_runs`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${user?.token}`,
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            setMessage('Oops! There was an error while creating your job. Please try again, or contact support if the issue persists.')
        }

        const result = await response.json();

        if (result.uuid) {
            setSubmitted(true)
            setMessage('Your job has been successfully created! Please allow a few minutes for the process to complete. Once it\'s finished, you will receive an email notification.Thank you for your patience, and we appreciate your understanding!')
            setTimeout(function () {
                console.log("30 seconds have passed!");
            }, 30000);
            //window.location.href = `/qpadm_runs/${result.uuid}`
        }
    }

    const styles = {
        pageContainer: {
            backgroundColor: '#f8f9fa',
            minHeight: '100vh',
            padding: '2rem 0',
        },
        header: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            borderRadius: '16px',
            padding: '3rem 2rem',
            color: 'white',
            marginBottom: '3rem',
            textAlign: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        headerTitle: {
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(45deg, #ffffff 30%, #e2e8f0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        },
        headerSubtitle: {
            fontSize: '1.1rem',
            opacity: '0.9',
            maxWidth: '800px',
            margin: '0 auto',
            color: '#e2e8f0',
        },
        card: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            height: '100%',
            background: '#ffffff',
        },
        cardHeader: {
            background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)',
            color: 'white',
            padding: '1.5rem',
            borderBottom: 'none',
        },
        cardTitle: {
            fontSize: '1.25rem',
            fontWeight: '600',
            margin: 0,
        },
        cardBody: {
            padding: '1.5rem',
        },
        input: {
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            fontSize: '1rem',
            transition: 'border-color 0.2s ease',
            '&:focus': {
                borderColor: '#4299E1',
                boxShadow: 'none',
            },
        },
        button: {
            padding: '0.75rem 1.5rem',
            fontWeight: '600',
            borderRadius: '8px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
        },
        sampleItem: {
            background: '#f8fafc',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '0.75rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            },
        },
        alert: {
            borderRadius: '12px',
            padding: '1rem 1.5rem',
            marginBottom: '2rem',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        },
    };

    return (

        <>
            <Helmet>
                <title>DNACloudHub | Run Page</title>
                <meta name="description" content="Run your custom qpAdm models offered by DNACloudHub." />
                <meta property="og:title" content={'DNACloudHub | Run Page'} />
                <meta property="og:description" content={'"Run your custom qpAdm models offered by DNACloudHub."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/run_qpadm`} />
            </Helmet>

            <div style={styles.pageContainer}>
                <Container>
                    {/* Header Section */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div style={styles.header}>
                            <h1 style={styles.headerTitle}>qpAdm Run Configuration</h1>
                            <p style={styles.headerSubtitle}>
                                Configure your qpAdm analysis by selecting populations and parameters for your genetic ancestry study
                            </p>
                        </div>
                    </motion.div>

                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <Alert
                                variant={submitted ? "success" : "danger"}
                                style={styles.alert}
                                onClose={() => setMessage('')}
                                dismissible
                            >
                                {message}
                            </Alert>
                        </motion.div>
                    )}

                    {/* Run Type Selection */}
                    <Row className="mb-4 g-4">
                        <Col md={6} lg={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <Card style={styles.card}>
                                    <Card.Header style={styles.cardHeader}>
                                        <h5 style={styles.cardTitle}>Run Type</h5>
                                    </Card.Header>
                                    <Card.Body style={styles.cardBody}>
                                        <Form.Group controlId="formRunType">
                                            <Form.Label className="fw-bold mb-3">Select Run Type</Form.Label>
                                            <Form.Select
                                                value={runType}
                                                onChange={(e) => setRunType(e.target.value)}
                                                style={styles.input}
                                            >
                                                <option value="static">Static</option>
                                                <option value="rotation" disabled>
                                                    Rotation (Coming soon)
                                                </option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>

                        <Col md={6} lg={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                            >
                                <Card style={styles.card}>
                                    <Card.Header style={styles.cardHeader}>
                                        <h5 style={styles.cardTitle}>Manage Rights</h5>
                                    </Card.Header>
                                    <Card.Body style={styles.cardBody}>
                                        <div className="d-grid gap-3">
                                            <Button
                                                variant="primary"
                                                style={styles.button}
                                                onClick={preloadRights}
                                                className="shadow-sm"
                                            >
                                                <FaRedo /> Preload Rights
                                            </Button>
                                            <Button
                                                variant="danger"
                                                style={styles.button}
                                                onClick={clearRights}
                                                className="shadow-sm"
                                            >
                                                <FaEraser /> Clear Rights
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>

                        {runType === 'rotation' && (
                            <Col md={6} lg={4}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                >
                                    <Card style={styles.card}>
                                        <Card.Header style={styles.cardHeader}>
                                            <h5 style={styles.cardTitle}>Number of Sources</h5>
                                        </Card.Header>
                                        <Card.Body style={styles.cardBody}>
                                            <Form.Group controlId="formRotation">
                                                <Form.Label className="fw-bold mb-3">Select Number of Sources</Form.Label>
                                                <Form.Select
                                                    value={populations}
                                                    onChange={(e) => setPopulations(e.target.value)}
                                                    style={styles.input}
                                                >
                                                    <option value={2}>2 Sources</option>
                                                    <option value={3}>3 Sources</option>
                                                    <option value={4}>4 Sources</option>
                                                    <option value={5}>5 Sources</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Card.Body>
                                    </Card>
                                </motion.div>
                            </Col>
                        )}
                    </Row>

                    {/* Sample Selection Section */}
                    {runType === 'static' ? (
                        <Row className="g-4">
                            {/* Left Samples */}
                            <Col md={6}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                >
                                    <Card style={styles.card}>
                                        <Card.Header style={styles.cardHeader}>
                                            <h5 style={styles.cardTitle}>Left Populations</h5>
                                        </Card.Header>
                                        <Card.Body style={styles.cardBody}>
                                            <InputGroup className="mb-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search samples"
                                                    value={searchTermLeft}
                                                    list="samples_left"
                                                    onChange={(e) => setSearchTermLeft(e.target.value)}
                                                    style={styles.input}
                                                />
                                                <datalist id="samples_left">
                                                    {samples.map((sample) => (
                                                        <option key={sample.sample_name} value={sample.sample_name}>
                                                            {sample.sample_name}
                                                        </option>
                                                    ))}
                                                </datalist>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleAddSample('left')}
                                                    style={styles.button}
                                                >
                                                    <FaPlus /> Add
                                                </Button>
                                            </InputGroup>
                                            <div className="samples-list">
                                                {leftSamples.map((sample, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        style={styles.sampleItem}
                                                    >
                                                        <span className="fw-medium">{sample}</span>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete('left', sample)}
                                                            style={{ ...styles.button, padding: '0.5rem' }}
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </motion.div>
                            </Col>

                            {/* Right Samples */}
                            <Col md={6}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                >
                                    <Card style={styles.card}>
                                        <Card.Header style={styles.cardHeader}>
                                            <h5 style={styles.cardTitle}>Right Populations</h5>
                                        </Card.Header>
                                        <Card.Body style={styles.cardBody}>
                                            <InputGroup className="mb-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search samples"
                                                    value={searchTermRight}
                                                    list="samples_right"
                                                    onChange={(e) => setSearchTermRight(e.target.value)}
                                                    style={styles.input}
                                                />
                                                <datalist id="samples_right">
                                                    {samples.map((sample) => (
                                                        <option key={sample.sample_name} value={sample.sample_name}>
                                                            {sample.sample_name}
                                                        </option>
                                                    ))}
                                                </datalist>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleAddSample('right')}
                                                    style={styles.button}
                                                >
                                                    <FaPlus /> Add
                                                </Button>
                                            </InputGroup>
                                            <div className="samples-list">
                                                {rightSamples.map((sample, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        style={styles.sampleItem}
                                                    >
                                                        <span className="fw-medium">{sample}</span>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            disabled={sample === "Mbuti.DG"}
                                                            onClick={() => handleDelete('right', sample)}
                                                            style={{ ...styles.button, padding: '0.5rem' }}
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </motion.div>
                            </Col>
                        </Row>
                    ) : (
                        <Row className="g-4">
                            {/* Core Right */}
                            <Col md={6}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                >
                                    <Card style={styles.card}>
                                        <Card.Header style={styles.cardHeader}>
                                            <h5 style={styles.cardTitle}>Core Right</h5>
                                        </Card.Header>
                                        <Card.Body style={styles.cardBody}>
                                            <InputGroup className="mb-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search samples"
                                                    value={searchTermRight}
                                                    list="samples_right"
                                                    onChange={(e) => setSearchTermRight(e.target.value)}
                                                    style={styles.input}
                                                />
                                                <datalist id="samples_right">
                                                    {samples.map((sample) => (
                                                        <option key={sample.sample_name} value={sample.sample_name}>
                                                            {sample.sample_name}
                                                        </option>
                                                    ))}
                                                </datalist>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleAddSample('right')}
                                                    style={styles.button}
                                                >
                                                    <FaPlus /> Add
                                                </Button>
                                            </InputGroup>
                                            <div className="samples-list">
                                                {rightSamples.map((sample, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        style={styles.sampleItem}
                                                    >
                                                        <span className="fw-medium">{sample}</span>
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete('right', sample)}
                                                            style={{ ...styles.button, padding: '0.5rem' }}
                                                        >
                                                            <FaTrash />
                                                        </Button>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </motion.div>
                            </Col>

                            {/* Sources */}
                            <Col md={6}>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                >
                                    <Card style={styles.card}>
                                        <Card.Header style={styles.cardHeader}>
                                            <h5 style={styles.cardTitle}>Sources</h5>
                                        </Card.Header>
                                        <Card.Body style={styles.cardBody}>
                                            <InputGroup className="mb-4">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Search samples"
                                                    value={searchTermRight}
                                                    list="samples_right"
                                                    onChange={(e) => setSearchTermRight(e.target.value)}
                                                    style={styles.input}
                                                />
                                                <datalist id="samples_right">
                                                    {samples.map((sample) => (
                                                        <option key={sample.sample_name} value={sample.sample_name}>
                                                            {sample.sample_name}
                                                        </option>
                                                    ))}
                                                </datalist>
                                                <Button
                                                    variant="primary"
                                                    onClick={() => handleAddSample('right')}
                                                    style={styles.button}
                                                >
                                                    <FaPlus /> Add
                                                </Button>
                                            </InputGroup>
                                            <div className="samples-list">
                                                {[...Array(Number(populations)).keys()].map((sample, index) => (
                                                    <motion.div
                                                        key={index}
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ duration: 0.3 }}
                                                        style={styles.sampleItem}
                                                    >
                                                        <span className="fw-medium">Source {index + 1}</span>
                                                        <Button
                                                            variant="success"
                                                            size="sm"
                                                            onClick={() => handleDelete('left', sample)}
                                                            style={{ ...styles.button, padding: '0.5rem' }}
                                                        >
                                                            <FaPlus />
                                                        </Button>
                                                    </motion.div>
                                                ))}
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </motion.div>
                            </Col>
                        </Row>
                    )}

                    {/* Run Button */}
                    <Row className="mt-5">
                        <Col className="text-center">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.7 }}
                            >
                                <Button
                                    variant="primary"
                                    size="lg"
                                    onClick={runJob}
                                    style={{
                                        ...styles.button,
                                        padding: '1rem 3rem',
                                        fontSize: '1.2rem',
                                    }}
                                    className="shadow"
                                    disabled={submitted}
                                >
                                    {submitted ? 'Processing...' : 'Run Analysis'}
                                </Button>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </>
    );
};

export default RunPage;
